// src/api.js
import axios from "axios";
import store from "./redux/store";
import { clearError, showError } from "./redux/error/errorActions";
import {
  clearUnpaidError,
  showUnpaidError,
} from "./redux/unpaidError/unpaidErrorActions";

// ----------------------------
// Axios instance
// ----------------------------
const api = axios.create({
  baseURL:
    process.env
      .REACT_APP_API_URL /* "https://apiraqib.vercel.app/api/"  */ /*  "http://localhost:5001/api/" */,
  withCredentials: true,
});

// ----------------------------
// Delay helper (optional)
// ----------------------------
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ----------------------------
// Error types to trigger logout
// ----------------------------
const ERROR_TYPES = [
  "invalidToken",
  "expiredAccessToken",
  "invalidAccessToken",
  "invalidRefreshToken",
  "expiredRefreshToken",
  "invalidAccessOrRefresh",
  "sessionInactive",
  "noToken",
  "invalidAppKey",
  "missingAppKey",
  "dangerousManipulation",
  "missingSessionToken",
  "invalidSession",
  "missingAllTokens",
];

const UNPAID_ERROR_TYPES = [
  "unpaidSubscription",
  "subscriptionPlanNotFound",
  "paymentRequired",
  "linkedPlanClinicNotFound",
];

const GEO_ERROR_TYPES = [
  "geoLocationError",
  "geoLocationTimeout",
  "geoLocationPermissionDenied",
  "geoLocationPositionUnavailable",
  "unknownLocation",
  "geoBlocked"
];

// ----------------------------
// Request blocking mechanism
// ----------------------------
const BLOCKED_ERROR_KEY = "api_blocked_error";

const setBlockedError = (errorType) => {
  try {
    localStorage.setItem(BLOCKED_ERROR_KEY, errorType);
  } catch (e) {
   
  }
};

let isRedirecting = false;

// const getBlockedError = () => {
//   return localStorage.getItem(BLOCKED_ERROR_KEY);
// };

// const clearBlockedError = () => {
//   localStorage.removeItem(BLOCKED_ERROR_KEY);
// };

// Export for manual clearing if needed
// export const unblockRequests = () => {
//   clearBlockedError();
// };

// ----------------------------
// Request deduplication setup
// ----------------------------
const pendingRequests = new Map();

const getRequestKey = (config) => {
  const { method, url, params, data } = config;
  return `${method}-${url}-${JSON.stringify(params || {})}-${JSON.stringify(
    data || {}
  )}`;
};

// ----------------------------
// Request interceptor
// ----------------------------
api.interceptors.request.use(
  async (config) => {
    //const blockedError = getBlockedError();
    // if (blockedError) {
    //   return Promise.reject({
    //     isBlocked: true,
    //     blockedBy: blockedError,
    //     message: `API requests are blocked due to ${blockedError} error`,
    //   });
    // }

    // Skip for OPTIONS requests
    if (config.method?.toUpperCase() === "OPTIONS") return config;

    const defaultLanguage = localStorage.getItem("language") || "en";
    
    // Don't set Content-Type for FormData - let axios handle it with proper boundary
    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = config.headers["Content-Type"] || "application/json";
    }
    
    config.headers["X-Content-Type-Options"] = "nosniff";
    config.headers["Accept-Language"] = defaultLanguage;
    
    if (config.delay) await delay(config.delay);

    // Skip deduplication for FormData requests since they contain files and can't be JSON stringified
    if (!(config.data instanceof FormData)) {
      const key = getRequestKey(config);
      if (pendingRequests.has(key)) {
        // Cancel the previous request with same key
        const cancel = pendingRequests.get(key);
        cancel("Duplicate request canceled.");
        pendingRequests.delete(key);
      }

      // Create a new AbortController for this request
      const controller = new AbortController();
      config.signal = controller.signal;
      pendingRequests.set(key, controller.abort.bind(controller));
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ----------------------------
// Response interceptor
// ----------------------------
api.interceptors.response.use(
  (response) => {
    // Only clean up deduplication tracking for non-FormData requests
    if (!(response.config.data instanceof FormData)) {
      const key = getRequestKey(response.config);
      pendingRequests.delete(key);
    }
    try {
          const error = localStorage.getItem(BLOCKED_ERROR_KEY);
          const unpaidError = localStorage.getItem('unpaidError');
          const ip = localStorage.getItem("ip");
          const country = localStorage.getItem("country");
          if (error) {
            store.dispatch(clearError());
            localStorage.removeItem(BLOCKED_ERROR_KEY);
          }
          if (unpaidError) {
            store.dispatch(clearUnpaidError());
            localStorage.removeItem('unpaidError');
          }
          if (ip || country) {
            store.dispatch(clearError());
            localStorage.removeItem("ip");
            localStorage.removeItem("country");
          }
        } catch (dispatchErr) {
          // Handle error silently
        }
    return response;
  },

  async (error) => {
    const originalRequest = error.config;

    if (originalRequest && !(originalRequest.data instanceof FormData)) {
      const key = getRequestKey(originalRequest);
      pendingRequests.delete(key);
    }

    if (!originalRequest) return Promise.reject(error);

    if (error.code === "ERR_CANCELED") {
      // Do nothing; silently ignore
      return new Promise(() => {}); // prevents triggering catch or re-render
    }

    if (
      error.code === "ERR_NETWORK" ||
      error.message.includes("ERR_CONNECTION_REFUSED")
    ) {
      return Promise.reject({
        response: {
          data: { error: "Server is offline. Please try again later." },
        },
      });
    }

    if (
      error.response &&
      (error.response.status === 401 ||
        error.response.status === 403 ||
        error.response.status === 402) 
    ) {
      const errType = error.response?.data?.type;

      if (ERROR_TYPES.includes(errType)) {
       

        setBlockedError(errType);
        try {
          const msg = error.response?.data?.message || "";
          // dispatch into redux so ErrorPage can read state
          store.dispatch(
            showError(
              errType || error.response?.data?.error || "auth_error",
              msg
            )
          );

          if (isRedirecting) {
            return new Promise(() => {});
          }

          isRedirecting = true;
          try {
            window.location.replace("/error");
          } catch (navErr) {
            
          }

          return new Promise(() => {});
        } catch (navErr) {
       
        }
      }
      
      if (UNPAID_ERROR_TYPES.includes(errType)) {
        originalRequest._retry = true;
        try {
          store.dispatch(clearError());
          window.location.replace(`${window.location.origin}/subscription`);
          return new Promise(() => {});
        } catch (navErr) {
          // Handle error silently
        }
      } else {
        try {
          store.dispatch(clearUnpaidError());
        } catch (navErr) {
          // Handle error silently
        }
      }

      if (GEO_ERROR_TYPES.includes(errType)) {
        originalRequest._retry = true;
        try {
          store.dispatch(
            showError("geoBlock", error.response?.data?.message || "", error.response?.data?.meta?.ip || "", error.response?.data?.meta?.country || "")
          );
          window.location.replace(`${window.location.origin}/`);
          return new Promise(() => {});
        }
        catch (navErr) {
          
        }
      }else {
        try {
          const blockedError = localStorage.getItem(BLOCKED_ERROR_KEY);
          const ip = localStorage.getItem("ip");
          const country = localStorage.getItem("country");
          const unpaidError = localStorage.getItem('unpaidError');
          if (blockedError) {
            store.dispatch(clearError());
            localStorage.removeItem(BLOCKED_ERROR_KEY);
          }

          if (ip || country) {
            store.dispatch(clearError());
            localStorage.removeItem("ip");
            localStorage.removeItem("country");
          }

          
          if (unpaidError) {
            store.dispatch(clearUnpaidError());
            localStorage.removeItem('unpaidError');
          }
        } catch (dispatchErr) {
          // Handle error silently
        }
      }
    }

    if (error.response?.status === 429) {
      return Promise.reject({
        response: {
          data: { error: "Too many requests. Please try again later." },
        },
      });
    }

    

    return Promise.reject(error);
  }
);

export default api;
